import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from './usePermissions';
import { getExternalDispatchPermissions } from '../utils/permissionHelpers';
import { ExternalDispatch, ExternalDispatchDirection } from '../types/inventory';
import { useDefaultPointOfSaleSite } from './useSites';
import { useCreateStockNotification } from './useNotifications';
import { getStockAlertThresholdForSite } from '../utils/stockAlerts';
import { getMissingDefaultSiteError } from '../utils/siteDefaults';
import { getProductDisplayName } from '../utils/productPackaging';

const getCurrentStockAtSite = (productData: any, siteId: string): number => {
  const stockBySite: Record<string, number> = productData.stockBySite || {};
  const hasStockBySite = Object.keys(stockBySite).length > 0;
  if (hasStockBySite) {
    return Number(stockBySite[siteId] ?? 0) || 0;
  }
  // Compat mode legacy mono-site: pas encore de répartition par site.
  return Number(productData.currentStock ?? 0) || 0;
};

type CreateExternalDispatchInput = {
  direction?: ExternalDispatchDirection;
  dispatchDate: Date;
  destinationId?: string;
  destinationName: string;
  destinationContact?: string;
  reference?: string;
  notes?: string;
  attachments?: string[];
  siteId?: string;
  siteName?: string;
  items: Array<{
    productId: string;
    quantity: number;
    unitPrice?: number;
  }>;
};

type UpdateExternalDispatchInput = {
  id: string;
  direction?: ExternalDispatchDirection;
  dispatchDate: Date;
  destinationId?: string;
  destinationName: string;
  destinationContact?: string;
  reference?: string;
  notes?: string;
  attachments?: string[];
  items: Array<{
    productId: string;
    quantity: number;
    unitPrice?: number;
  }>;
};

export const useExternalDispatches = () => {
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;
  const permissions = usePermissions();
  const perms = getExternalDispatchPermissions(permissions);

  return useQuery({
    queryKey: ['inventory', 'externalDispatches', businessId],
    queryFn: async () => {
      if (!businessId) return [];

      const q = query(
        collection(db, COLLECTIONS.externalDispatches),
        where('businessId', '==', businessId),
        orderBy('dispatchDate', 'desc'),
        limit(100)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((dispatchDoc) => {
        const data = dispatchDoc.data();
        const direction: ExternalDispatchDirection =
          data.direction === 'inbound' ? 'inbound' : 'outbound';
        return {
          id: dispatchDoc.id,
          businessId: data.businessId,
          direction,
          dispatchDate: data.dispatchDate?.toDate?.() ?? new Date(),
          destinationId: data.destinationId,
          destinationName: data.destinationName,
          destinationContact: data.destinationContact,
          reference: data.reference,
          notes: data.notes,
          attachments: Array.isArray(data.attachments) ? data.attachments : [],
          siteId: data.siteId,
          siteName: data.siteName,
          items: data.items ?? [],
          totalQuantity: data.totalQuantity ?? 0,
          totalAmount: data.totalAmount ?? 0,
          status: data.status ?? (direction === 'inbound' ? 'received' : 'sent'),
          createdBy: data.createdBy,
          createdByName: data.createdByName,
          createdAt: data.createdAt?.toDate?.() ?? new Date(),
          updatedAt: data.updatedAt?.toDate?.(),
        } as ExternalDispatch;
      });
    },
    enabled: !!businessId && perms.canView,
  });
};

export const useAddExternalDispatch = () => {
  const queryClient = useQueryClient();
  const { currentBusiness, userProfile } = useAuth();
  const businessId = currentBusiness?.id;
  const defaultPosSite = useDefaultPointOfSaleSite(businessId);
  const permissions = usePermissions();
  const perms = getExternalDispatchPermissions(permissions);
  const createStockNotification = useCreateStockNotification();

  return useMutation({
    mutationFn: async (payload: CreateExternalDispatchInput) => {
      if (!businessId || !userProfile) {
        throw new Error('Institut ou utilisateur non défini');
      }
      if (!perms.canCreate) {
        throw new Error('Vous n\'avez pas la permission de créer un bon d\'envoi externe');
      }
      if (!payload.destinationName?.trim()) {
        throw new Error('Le destinataire est obligatoire');
      }
      if (!payload.items?.length) {
        throw new Error('Ajoutez au moins un produit');
      }

      const resolvedSiteId = payload.siteId || defaultPosSite?.id;
      const resolvedSiteName = payload.siteName || defaultPosSite?.name;
      if (!resolvedSiteId || !resolvedSiteName) {
        throw new Error(getMissingDefaultSiteError(currentBusiness));
      }
      const normalizedDirection: ExternalDispatchDirection =
        payload.direction === 'inbound' ? 'inbound' : 'outbound';

      const now = Timestamp.now();
      const batch = writeBatch(db);
      const externalDispatchRef = doc(collection(db, COLLECTIONS.externalDispatches));

      const normalizedItems: Array<{
        productId: string;
        productName: string;
        quantity: number;
        unitPrice?: number;
        totalPrice?: number;
      }> = [];

      let totalQuantity = 0;
      let totalAmount = 0;

      const notifications: Array<{
        productId: string;
        productName: string;
        currentStock: number;
        minStockAlert: number;
        siteId?: string;
        siteName?: string;
      }> = [];

      for (const rawItem of payload.items) {
        const quantity = Number(rawItem.quantity) || 0;
        if (!rawItem.productId || quantity <= 0) continue;

        const productRef = doc(db, COLLECTIONS.products, rawItem.productId);
        const productSnap = await getDoc(productRef);
        if (!productSnap.exists()) {
          throw new Error('Un des produits est introuvable');
        }

        const productData = productSnap.data() as any;
        if (productData.businessId !== businessId) {
          throw new Error(`Le produit ${productData.name ?? rawItem.productId} n'appartient pas au business`);
        }

        const stockBySite: Record<string, number> = productData.stockBySite || {};
        const currentAtSite = getCurrentStockAtSite(productData, resolvedSiteId);
        if (normalizedDirection === 'outbound' && quantity > currentAtSite) {
          throw new Error(`Stock insuffisant pour ${productData.name}. Disponible: ${currentAtSite}, demandé: ${quantity}`);
        }

        const newAtSite =
          normalizedDirection === 'outbound'
            ? Math.max(0, currentAtSite - quantity)
            : currentAtSite + quantity;
        const updatedStockBySite: Record<string, number> = {
          ...stockBySite,
          [resolvedSiteId]: newAtSite,
        };
        const newCurrentStock = Object.values(updatedStockBySite).reduce<number>((sum, value) => sum + (Number(value) || 0), 0);

        batch.update(productRef, {
          stockBySite: updatedStockBySite,
          currentStock: newCurrentStock,
          updatedAt: now,
        });

        const unitPrice = rawItem.unitPrice != null ? Number(rawItem.unitPrice) : undefined;
        const totalPrice = unitPrice != null ? unitPrice * quantity : undefined;
        const movementRef = doc(collection(db, COLLECTIONS.stockMovements));
        const movementType = normalizedDirection === 'outbound' ? 'exit' : 'entry';
        const movementReason =
          normalizedDirection === 'outbound'
            ? `Bon d'envoi externe: ${resolvedSiteName} -> ${payload.destinationName.trim()}`
            : `Bon d'entrée externe: ${payload.destinationName.trim()} -> ${resolvedSiteName}`;
        batch.set(movementRef, {
          businessId,
          productId: rawItem.productId,
          productName: getProductDisplayName(productData),
          type: movementType,
          quantity,
          reason: movementReason,
          date: Timestamp.fromDate(payload.dispatchDate),
          employeeId: userProfile.id,
          employeeName: userProfile.name,
          createdAt: now,
          siteId: resolvedSiteId,
          siteName: resolvedSiteName,
          externalDispatchId: externalDispatchRef.id,
          externalDestinationName: payload.destinationName.trim(),
          externalDestinationContact: payload.destinationContact?.trim() || null,
          externalReference: payload.reference?.trim() || null,
          ...(unitPrice != null ? { unitCost: unitPrice, totalCost: totalPrice } : {}),
        });

        normalizedItems.push({
          productId: rawItem.productId,
          productName: getProductDisplayName(productData),
          quantity,
          ...(unitPrice != null ? { unitPrice } : {}),
          ...(totalPrice != null ? { totalPrice } : {}),
        });
        totalQuantity += quantity;
        if (totalPrice != null) totalAmount += totalPrice;

        notifications.push({
          productId: rawItem.productId,
          productName: getProductDisplayName(productData),
          currentStock: newAtSite,
          minStockAlert: getStockAlertThresholdForSite(productData, resolvedSiteId),
          siteId: resolvedSiteId,
          siteName: resolvedSiteName,
        });
      }

      if (!normalizedItems.length) {
        throw new Error('Aucun produit valide à envoyer');
      }

      batch.set(externalDispatchRef, {
        businessId,
        direction: normalizedDirection,
        dispatchDate: Timestamp.fromDate(payload.dispatchDate),
        destinationId: payload.destinationId || null,
        destinationName: payload.destinationName.trim(),
        destinationContact: payload.destinationContact?.trim() || null,
        reference: payload.reference?.trim() || null,
        notes: payload.notes?.trim() || null,
        attachments: payload.attachments || [],
        siteId: resolvedSiteId,
        siteName: resolvedSiteName,
        items: normalizedItems,
        totalQuantity,
        totalAmount,
        status: normalizedDirection === 'inbound' ? 'received' : 'sent',
        createdBy: userProfile.id,
        createdByName: userProfile.name,
        createdAt: now,
        updatedAt: now,
      });

      await batch.commit();

      await Promise.all(
        notifications.map((notification) =>
          createStockNotification.mutateAsync(notification).catch((error) => {
            console.warn('Erreur notification stock après sortie externe:', error);
          })
        )
      );

      return externalDispatchRef.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'externalDispatches', businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'stockMovements', businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products', businessId] });
    },
  });
};

export const useUpdateExternalDispatch = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;
  const permissions = usePermissions();
  const perms = getExternalDispatchPermissions(permissions);
  const createStockNotification = useCreateStockNotification();

  return useMutation({
    mutationFn: async (payload: UpdateExternalDispatchInput) => {
      if (!businessId) {
        throw new Error('Institut non défini');
      }
      if (!perms.canUpdate) {
        throw new Error('Vous n\'avez pas la permission de modifier un bon d\'envoi externe');
      }
      if (!payload.items?.length) {
        throw new Error('Ajoutez au moins un produit');
      }
      const normalizedDirection: ExternalDispatchDirection =
        payload.direction === 'inbound' ? 'inbound' : 'outbound';

      const externalDispatchRef = doc(db, COLLECTIONS.externalDispatches, payload.id);
      const dispatchSnap = await getDoc(externalDispatchRef);
      if (!dispatchSnap.exists()) {
        throw new Error('Bon d\'envoi introuvable');
      }

      const dispatchData = dispatchSnap.data() as any;
      const previousDirection: ExternalDispatchDirection =
        dispatchData.direction === 'inbound' ? 'inbound' : 'outbound';
      if (dispatchData.businessId !== businessId) {
        throw new Error('Ce bon d\'envoi n\'appartient pas au business actuel');
      }
      const siteId: string | undefined = dispatchData.siteId;
      const siteName: string | undefined = dispatchData.siteName;
      if (!siteId) {
        throw new Error('Lieu source introuvable sur ce bon d\'envoi');
      }

      const previousItems = Array.isArray(dispatchData.items) ? dispatchData.items : [];

      const normalizedItems = payload.items
        .map((item) => ({
          productId: String(item.productId || ''),
          quantity: Number(item.quantity) || 0,
          unitPrice: item.unitPrice != null ? Number(item.unitPrice) : undefined,
        }))
        .filter((item) => !!item.productId && item.quantity > 0);
      if (!normalizedItems.length) {
        throw new Error('Aucun produit valide');
      }

      const batch = writeBatch(db);
      const now = Timestamp.now();
      const normalizedDestination = payload.destinationName.trim();
      const normalizedContact = payload.destinationContact?.trim() || null;
      const normalizedReference = payload.reference?.trim() || null;

      const previousStockEffectByProduct = new Map<string, number>();
      const previousSign = previousDirection === 'outbound' ? -1 : 1;
      previousItems.forEach((item: any) => {
        const productId = String(item?.productId || '');
        const quantity = Number(item?.quantity) || 0;
        if (!productId || quantity <= 0) return;
        previousStockEffectByProduct.set(
          productId,
          (previousStockEffectByProduct.get(productId) || 0) + quantity * previousSign
        );
      });

      const nextStockEffectByProduct = new Map<string, number>();
      const nextSign = normalizedDirection === 'outbound' ? -1 : 1;
      normalizedItems.forEach((item) => {
        nextStockEffectByProduct.set(
          item.productId,
          (nextStockEffectByProduct.get(item.productId) || 0) + item.quantity * nextSign
        );
      });

      const touchedProductIds = new Set<string>([
        ...Array.from(previousStockEffectByProduct.keys()),
        ...Array.from(nextStockEffectByProduct.keys()),
      ]);

      const notifications: Array<{
        productId: string;
        productName: string;
        currentStock: number;
        minStockAlert: number;
        siteId?: string;
        siteName?: string;
      }> = [];

      const productNameById = new Map<string, string>();

      for (const productId of Array.from(touchedProductIds)) {
        const previousEffect = previousStockEffectByProduct.get(productId) || 0;
        const nextEffect = nextStockEffectByProduct.get(productId) || 0;
        const deltaStock = nextEffect - previousEffect;
        if (deltaStock === 0) continue;

        const productRef = doc(db, COLLECTIONS.products, productId);
        const productSnap = await getDoc(productRef);
        if (!productSnap.exists()) {
          throw new Error(`Produit introuvable: ${productId}`);
        }
        const productData = productSnap.data() as any;
        if (productData.businessId !== businessId) {
          throw new Error(`Le produit ${productData.name ?? productId} n'appartient pas au business actuel`);
        }
        productNameById.set(productId, getProductDisplayName(productData));

        const stockBySite: Record<string, number> = productData.stockBySite || {};
        const currentAtSite = getCurrentStockAtSite(productData, siteId);
        const nextAtSite = currentAtSite + deltaStock;
        if (nextAtSite < 0) {
          throw new Error(
            `Stock insuffisant pour ${productData.name}. Disponible: ${currentAtSite}, variation demandée: ${deltaStock}`
          );
        }

        const updatedStockBySite: Record<string, number> = {
          ...stockBySite,
          [siteId]: nextAtSite,
        };
        const newCurrentStock = Object.values(updatedStockBySite).reduce<number>(
          (sum, value) => sum + (Number(value) || 0),
          0
        );

        batch.update(productRef, {
          stockBySite: updatedStockBySite,
          currentStock: newCurrentStock,
          updatedAt: now,
        });

        notifications.push({
          productId,
          productName: getProductDisplayName(productData),
          currentStock: nextAtSite,
          minStockAlert: getStockAlertThresholdForSite(productData, siteId),
          siteId,
          siteName,
        });
      }

      batch.update(externalDispatchRef, {
        direction: normalizedDirection,
        dispatchDate: Timestamp.fromDate(payload.dispatchDate),
        destinationId: payload.destinationId || null,
        destinationName: normalizedDestination,
        destinationContact: normalizedContact,
        reference: normalizedReference,
        notes: payload.notes?.trim() || null,
        attachments: payload.attachments || [],
        items: normalizedItems.map((item) => {
          const totalPrice = item.unitPrice != null ? item.unitPrice * item.quantity : undefined;
          return {
            productId: item.productId,
            productName: productNameById.get(item.productId) || item.productId,
            quantity: item.quantity,
            ...(item.unitPrice != null ? { unitPrice: item.unitPrice } : {}),
            ...(totalPrice != null ? { totalPrice } : {}),
          };
        }),
        totalQuantity: normalizedItems.reduce((sum, item) => sum + item.quantity, 0),
        totalAmount: normalizedItems.reduce((sum, item) => {
          const itemTotal = item.unitPrice != null ? item.unitPrice * item.quantity : 0;
          return sum + itemTotal;
        }, 0),
        status: normalizedDirection === 'inbound' ? 'received' : 'sent',
        updatedAt: now,
      });

      const linkedMovementsSnap = await getDocs(
        query(collection(db, COLLECTIONS.stockMovements), where('externalDispatchId', '==', payload.id))
      );
      linkedMovementsSnap.docs.forEach((movementDoc) => {
        const movementData = movementDoc.data() as any;
        if (movementData.businessId !== businessId) return;
        batch.delete(movementDoc.ref);
      });

      for (const item of normalizedItems) {
        const movementRef = doc(collection(db, COLLECTIONS.stockMovements));
        const unitPrice = item.unitPrice;
        const totalPrice = unitPrice != null ? unitPrice * item.quantity : undefined;
        const movementType = normalizedDirection === 'outbound' ? 'exit' : 'entry';
        const movementReason =
          normalizedDirection === 'outbound'
            ? `Bon d'envoi externe: ${siteName || 'Lieu source'} -> ${normalizedDestination}`
            : `Bon d'entrée externe: ${normalizedDestination} -> ${siteName || 'Lieu source'}`;
        batch.set(movementRef, {
          businessId,
          productId: item.productId,
          productName: productNameById.get(item.productId) || item.productId,
          type: movementType,
          quantity: item.quantity,
          reason: movementReason,
          date: Timestamp.fromDate(payload.dispatchDate),
          employeeId: dispatchData.createdBy,
          employeeName: dispatchData.createdByName,
          createdAt: now,
          siteId,
          siteName,
          externalDispatchId: payload.id,
          externalDestinationName: normalizedDestination,
          externalDestinationContact: normalizedContact,
          externalReference: normalizedReference,
          ...(unitPrice != null ? { unitCost: unitPrice, totalCost: totalPrice } : {}),
        });
      }

      await batch.commit();

      await Promise.all(
        notifications.map((notification) =>
          createStockNotification.mutateAsync(notification).catch((error) => {
            console.warn('Erreur notification stock après modification d\'un bon externe:', error);
          })
        )
      );

      return payload.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'externalDispatches', businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'stockMovements', businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products', businessId] });
    },
  });
};

export const useDeleteExternalDispatch = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;
  const permissions = usePermissions();
  const perms = getExternalDispatchPermissions(permissions);
  const createStockNotification = useCreateStockNotification();

  return useMutation({
    mutationFn: async (externalDispatchId: string) => {
      if (!businessId) {
        throw new Error('Institut non défini');
      }
      if (!perms.canDelete) {
        throw new Error('Vous n\'avez pas la permission de supprimer un bon d\'envoi externe');
      }

      const externalDispatchRef = doc(db, COLLECTIONS.externalDispatches, externalDispatchId);
      const dispatchSnap = await getDoc(externalDispatchRef);
      if (!dispatchSnap.exists()) {
        throw new Error('Bon d\'envoi introuvable');
      }

      const dispatchData = dispatchSnap.data() as any;
      const dispatchDirection: ExternalDispatchDirection =
        dispatchData.direction === 'inbound' ? 'inbound' : 'outbound';
      if (dispatchData.businessId !== businessId) {
        throw new Error('Ce bon d\'envoi n\'appartient pas au business actuel');
      }

      const siteId: string | undefined = dispatchData.siteId;
      const siteName: string | undefined = dispatchData.siteName;
      if (!siteId) {
        throw new Error('Lieu source introuvable sur ce bon d\'envoi');
      }

      const items = Array.isArray(dispatchData.items) ? dispatchData.items : [];
      if (items.length === 0) {
        throw new Error('Ce bon d\'envoi ne contient aucun article');
      }

      const batch = writeBatch(db);
      const now = Timestamp.now();
      const notifications: Array<{
        productId: string;
        productName: string;
        currentStock: number;
        minStockAlert: number;
        siteId?: string;
        siteName?: string;
      }> = [];

      for (const item of items) {
        const productId = String(item?.productId || '');
        const quantity = Number(item?.quantity) || 0;
        if (!productId || quantity <= 0) continue;

        const productRef = doc(db, COLLECTIONS.products, productId);
        const productSnap = await getDoc(productRef);
        if (!productSnap.exists()) {
          throw new Error(`Produit introuvable pour restauration: ${productId}`);
        }
        const productData = productSnap.data() as any;
        if (productData.businessId !== businessId) {
          throw new Error(`Le produit ${productData.name ?? productId} n'appartient pas au business actuel`);
        }

        const stockBySite: Record<string, number> = productData.stockBySite || {};
        const currentAtSite = getCurrentStockAtSite(productData, siteId);
        const stockEffect = dispatchDirection === 'outbound' ? -quantity : quantity;
        const restoredAtSite = currentAtSite - stockEffect;
        if (restoredAtSite < 0) {
          throw new Error(
            `Stock insuffisant pour annuler ce bon sur ${productData.name}. Stock actuel: ${currentAtSite}`
          );
        }
        const updatedStockBySite = {
          ...stockBySite,
          [siteId]: restoredAtSite,
        };
        const newCurrentStock = Object.values(updatedStockBySite).reduce<number>(
          (sum, value) => sum + (Number(value) || 0),
          0
        );

        batch.update(productRef, {
          stockBySite: updatedStockBySite,
          currentStock: newCurrentStock,
          updatedAt: now,
        });

        notifications.push({
          productId,
          productName: getProductDisplayName(productData),
          currentStock: restoredAtSite,
          minStockAlert: getStockAlertThresholdForSite(productData, siteId),
          siteId,
          siteName,
        });
      }

      const linkedMovementsSnap = await getDocs(
        query(collection(db, COLLECTIONS.stockMovements), where('externalDispatchId', '==', externalDispatchId))
      );
      linkedMovementsSnap.docs.forEach((movementDoc) => {
        const movementData = movementDoc.data() as any;
        if (movementData.businessId !== businessId) return;
        batch.delete(movementDoc.ref);
      });

      batch.delete(externalDispatchRef);
      await batch.commit();

      await Promise.all(
        notifications.map((notification) =>
          createStockNotification.mutateAsync(notification).catch((error) => {
            console.warn('Erreur notification stock après suppression d\'un bon externe:', error);
          })
        )
      );

      return externalDispatchId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'externalDispatches', businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'stockMovements', businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products', businessId] });
    },
  });
};
